<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAppStore } from '../store'
import ScreenTitle from '../components/ScreenTitle.vue'
import { db } from '../db'
import type { Locale } from '../i18n'
import type { DayLook, RefLevel, Theme } from '../store'
import MeasureStepper from '../components/MeasureStepper.vue'
import Registration from '../components/Registration.vue'
import { MEASURES, SURGE, type Measure } from '../lib/boat'
import { heldBytes, freePlates } from '../lib/plates'
import { APP_CHANGES } from '../update'
import { fix as devicePosition, state as positionState, ask as askPosition } from '../lib/position'

const store = useAppStore()
const seriesCount = ref(0)
/** The day the running version was committed; a build without git or VERSION has none */
const released = computed(() => (store.appDate ? new Date(store.appDate).toLocaleDateString(store.settings.locale, { dateStyle: 'long' }) : ''))
const bytes = ref(0)
/** The changes, by day: "31 okt" with what came that day */
const changes = computed(() => APP_CHANGES.map(c => ({
  ...c,
  day: c.date ? new Date(c.date).toLocaleDateString(store.settings.locale, { day: 'numeric', month: 'short' }) : ''
})))

/**
 * The four measurements are set once and then left alone, so the steppers do not need to
 * be on screen the whole time — a row that reads "Diepgang 0.70 m" says everything until
 * it is wrong. Tapping the figure brings out the buttons.
 */
const editing = ref<Measure | null>(null)

const plates = ref({ atlases: 0, bytes: 0 })
onMounted(async () => {
  const rows = await db.series.toArray()
  seriesCount.value = rows.length
  bytes.value = rows.reduce((n, r) => n + r.bytes.byteLength, 0)
  plates.value = await heldBytes()
})

/** The plates can always be fetched again; giving the room back is safe */
async function dropPlates() {
  await freePlates()
  plates.value = await heldBytes()
}

const locales: { code: Locale; label: string }[] = [{ code: 'nl', label: 'NL' }, { code: 'en', label: 'EN' }, { code: 'de', label: 'DE' }]
const refLevels: RefLevel[] = ['NAP', 'LAT']
const themes: Theme[] = ['auto', 'day', 'dusk', 'night']
const dayLooks: { look: DayLook; label: 'lookDevice' | 'lookLight' | 'lookDark' }[] = [
  { look: 'device', label: 'lookDevice' }, { look: 'light', label: 'lookLight' }, { look: 'dark', label: 'lookDark' }
]

/**
 * What the last press of "update now" found, so the button answers rather than just stopping.
 * New measured levels count as something new: they are what a skipper coming back into range
 * presses the button for, and "nothing new" over a chart that has just grown its red line
 * reads as the app not knowing what it did.
 */
const outcome = ref<string>('')
async function updateNow() {
  outcome.value = ''
  const { levels } = await store.synchronise()
  if (store.sync.state !== 'done') return
  const said = [
    store.sync.total > 0 ? store.tv('dataUpdated', String(store.sync.total)) : '',
    levels ? store.t('levelsUpdated') : ''
  ].filter(Boolean)
  outcome.value = said.length ? said.join(' ') : store.updateAvailable ? '' : store.t('nothingNew')
}

/**
 * The ☰ menu opens this screen at a section -- the boat, the activation, the storage -- and
 * its "update" entry presses the button as well. The screen scrolls inside itself, not the
 * window, so the browser's own jump to the anchor does nothing and this does it instead.
 */
const route = useRoute()
const router = useRouter()
watch(() => route.fullPath, async () => {
  await nextTick()
  if (route.hash) document.querySelector(route.hash)?.scrollIntoView({ block: 'start' })
  if (route.query['bijwerken']) {
    // once: a reload or a step back must not update again
    void router.replace({ path: route.path, hash: route.hash })
    void updateNow()
  }
}, { immediate: true })

function fmt(iso: string | null) {
  return iso ? new Date(iso).toLocaleString(store.settings.locale, { dateStyle: 'short', timeStyle: 'short' }) : store.t('neverSynced')
}
</script>

<template>
  <main class="screen">
    <div class="header">
      <ScreenTitle>{{ store.t('settings') }}</ScreenTitle>
    </div>
    <div class="setting">
      <span>{{ store.t('refLevel') }}</span>
      <div class="segment">
        <button v-for="r in refLevels" :key="r" :class="{ active: store.settings.refLevel === r }" @click="store.settings.refLevel = r">{{ r === 'NAP' ? store.napDatumName : store.altDatumName }}</button>
      </div>
    </div>
    <div class="setting">
      <span>{{ store.t('language') }}</span>
      <div class="segment">
        <button v-for="l in locales" :key="l.code" :class="{ active: store.settings.locale === l.code }" @click="store.settings.locale = l.code">{{ l.label }}</button>
      </div>
    </div>
    <div class="setting">
      <span>{{ store.t('theme') }}</span>
      <div class="segment">
        <button v-for="th in themes" :key="th" :class="{ active: store.settings.theme === th }" @click="store.settings.theme = th">{{ store.t(th) }}</button>
      </div>
    </div>
    <!-- dusk and night look the same either way, so the choice only shows where day can -->
    <div v-if="store.settings.theme === 'auto' || store.settings.theme === 'day'" class="setting">
      <span>{{ store.t('dayLook') }}</span>
      <div class="segment">
        <button v-for="d in dayLooks" :key="d.look" :class="{ active: store.settings.dayLook === d.look }" @click="store.settings.dayLook = d.look">{{ store.t(d.label) }}</button>
      </div>
    </div>
    <div v-if="store.settings.theme === 'auto'" class="setting">
      <span class="small muted">
        {{ store.tv('themeFollowsSun', store.t(store.showingTheme)) }}
        {{ devicePosition ? store.t('sunAtPhone') : store.t('sunAtStation') }}
      </span>
      <button v-if="!devicePosition" class="link" :disabled="positionState === 'asking'" @click="askPosition()">
        {{ positionState === 'asking' ? store.t('positionAsking') : store.t('useMyPosition') }}
      </button>
    </div>
    <Registration />

    <h2 id="schip">{{ store.t('boat') }}</h2>
    <div v-for="m in MEASURES" :key="m.key" class="setting">
      <span>{{ store.t(m.label) }}</span>
      <button class="num measurevalue" @click="editing = m">{{ (store.settings[m.key] / 100).toFixed(2) }} m</button>
    </div>
    <h2>{{ store.t('water') }}</h2>
    <div class="setting">
      <span>{{ store.t('surgeSetting') }}</span>
      <button class="num measurevalue" @click="editing = SURGE">{{ (store.settings.surge / 100).toFixed(2) }} m</button>
    </div>
    <p v-if="store.settings.surge" class="notice warn small">{{ store.tv('surgeInEffect', (store.settings.surge / 100).toFixed(2)) }}</p>

    <h2 id="opslag">{{ store.t('storage') }}</h2>
    <div class="setting">
      <span>{{ store.t('lastSync') }}</span>
      <span class="num">{{ fmt(store.lastSync) }}</span>
    </div>
    <div class="setting">
      <span>{{ store.stations.length }} {{ store.t('stations') }}, {{ seriesCount }} series</span>
      <span class="num">{{ (bytes / 1_048_576).toFixed(1) }} MB</span>
    </div>
    <div v-if="plates.atlases" class="setting">
      <span>{{ plates.atlases }} {{ store.t('atlasesHeld') }}</span>
      <span class="stepper">
        <span class="num value">{{ (plates.bytes / 1_048_576).toFixed(0) }} MB</span>
        <button class="link" @click="dropPlates()">{{ store.t('freePlates') }}</button>
      </span>
    </div>
    <div class="setting">
      <span class="small muted">{{ outcome }}</span>
      <button class="primary" :disabled="store.sync.state === 'running'" @click="updateNow()">{{ store.t('updateNow') }}</button>
    </div>
    <div class="setting">
      <span>{{ store.t('version') }}</span>
      <span class="num">{{ store.appVersion }}<template v-if="released"> · {{ store.t('released') }} {{ released }}</template><template v-if="store.updateAvailable"> → {{ store.serverVersion }} · {{ store.t('updating') }}</template></span>
    </div>
    <details v-if="changes.length" class="changes">
      <summary>{{ store.t('whatsNew') }}</summary>
      <ul>
        <li v-for="c in changes" :key="c.hash"><span class="num muted">{{ c.day }}</span> {{ c.text }}</li>
      </ul>
    </details>
    <div class="setting">
      <span>{{ store.t('manual') }}</span>
      <a class="link" href="/help/" target="_blank" rel="noopener">{{ store.t('openManual') }}</a>
    </div>
    <p class="notice small">{{ store.t('about') }}</p>

    <div v-if="editing" class="modal" @click.self="editing = null">
      <div class="card">
        <h1>{{ store.t(editing.label) }}</h1>
        <p>{{ store.t(editing.help) }}</p>
        <div class="measure">
          <MeasureStepper v-model="store.settings[editing.key]" :coarse="editing.coarse" :fine="editing.fine"
                          :min="editing.min" :max="editing.max" />
        </div>
        <div class="cardbuttons">
          <span></span>
          <button class="primary big" @click="editing = null">{{ store.t('done') }}</button>
        </div>
      </div>
    </div>
  </main>
</template>
